import type { TypedSupabaseClient } from '../client';
import type {
  IFormationPageRepository,
  FormationPage,
  CreateFormationPageInput,
  UpdateFormationPageInput,
  FormationAudience,
  FormationVideo,
  FormationVideoProvider,
  CreateFormationVideoInput,
  UpdateFormationVideoInput,
  ProFormationChapter,
  ProFormationLesson,
  FormationCourseStat,
} from '../../../interfaces/src';

type LooseClient = {
  from: (t: string) => any;
  rpc: (fn: string, args?: Record<string, unknown>) => any;
};

export class SupabaseFormationPageRepository implements IFormationPageRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  private get db(): LooseClient {
    return this.supabase as unknown as LooseClient;
  }

  async canAuthor(): Promise<boolean> {
    const { data, error } = await this.db.rpc('formation_can_author');
    if (error) throw error;
    return data === true;
  }

  async list(): Promise<FormationPage[]> {
    const { data, error } = await this.db
      .from('formation_pages')
      .select('*')
      .order('display_order')
      .order('created_at');
    if (error) throw error;
    return (data ?? []).map(mapFormationPage);
  }

  async listPublished(): Promise<FormationPage[]> {
    const { data, error } = await this.db
      .from('formation_pages')
      .select('*')
      .eq('is_published', true)
      .order('display_order')
      .order('created_at');
    if (error) throw error;
    return (data ?? []).map(mapFormationPage);
  }

  async getById(id: string): Promise<FormationPage | null> {
    const { data, error } = await this.db
      .from('formation_pages')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapFormationPage(data) : null;
  }

  async getBySlug(slug: string): Promise<FormationPage | null> {
    const { data, error } = await this.db
      .from('formation_pages')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw error;
    return data ? mapFormationPage(data) : null;
  }

  async create(input: CreateFormationPageInput): Promise<FormationPage> {
    const { data, error } = await this.db
      .from('formation_pages')
      .insert(mapFormationPagePatch(input))
      .select()
      .single();
    if (error) throw error;
    return mapFormationPage(data);
  }

  async update(id: string, input: UpdateFormationPageInput): Promise<FormationPage> {
    const { data, error } = await this.db
      .from('formation_pages')
      .update(mapFormationPagePatch(input))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapFormationPage(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.db.from('formation_pages').delete().eq('id', id);
    if (error) throw error;
  }

  async reorder(ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await this.db
        .from('formation_pages')
        .update({ display_order: i })
        .eq('id', ids[i]);
      if (error) throw error;
    }
  }

  // ============================================================
  // Leçons vidéo
  // ============================================================

  async listVideos(pageId?: string): Promise<FormationVideo[]> {
    let query = this.db.from('formation_videos').select('*');
    if (pageId) query = query.eq('page_id', pageId);
    const { data, error } = await query.order('display_order').order('created_at');
    if (error) throw error;
    return (data ?? []).map(mapFormationVideo);
  }

  async createVideo(input: CreateFormationVideoInput): Promise<FormationVideo> {
    const { data, error } = await this.db
      .from('formation_videos')
      .insert(mapFormationVideoPatch(input))
      .select()
      .single();
    if (error) throw error;
    return mapFormationVideo(data);
  }

  async updateVideo(id: string, input: UpdateFormationVideoInput): Promise<FormationVideo> {
    const { data, error } = await this.db
      .from('formation_videos')
      .update(mapFormationVideoPatch(input))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapFormationVideo(data);
  }

  async deleteVideo(id: string): Promise<void> {
    const { error } = await this.db.from('formation_videos').delete().eq('id', id);
    if (error) throw error;
  }

  async reorderVideos(ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await this.db
        .from('formation_videos')
        .update({ display_order: i })
        .eq('id', ids[i]);
      if (error) throw error;
    }
  }

  async courseStats(): Promise<FormationCourseStat[]> {
    const { data, error } = await this.db.rpc('formation_course_stats');
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
      pageId: row['page_id'] as string,
      videoCount: (row['video_count'] as number) ?? 0,
      proTotal: (row['pro_total'] as number) ?? 0,
      proStarted: (row['pro_started'] as number) ?? 0,
      proCompleted: (row['pro_completed'] as number) ?? 0,
    }));
  }

  // ============================================================
  // Espace Pro
  // ============================================================

  async getProCourse(): Promise<ProFormationChapter[]> {
    const { data, error } = await this.db.rpc('pro_formation_course');
    if (error) throw error;

    // La RPC répond à plat (un enregistrement par leçon, chapitre répété).
    // On regroupe ici : l'ORDER BY côté SQL garantit que les leçons d'un
    // même chapitre arrivent contiguës et déjà dans l'ordre.
    const chapters = new Map<string, ProFormationChapter>();
    for (const row of (data ?? []) as Record<string, unknown>[]) {
      const chapterId = row['chapter_id'] as string;
      let chapter = chapters.get(chapterId);
      if (!chapter) {
        chapter = {
          id: chapterId,
          slug: (row['chapter_slug'] as string) ?? '',
          titleFr: (row['chapter_title_fr'] as string) ?? null,
          titleAr: (row['chapter_title_ar'] as string) ?? null,
          titleEn: (row['chapter_title_en'] as string) ?? null,
          displayOrder: (row['chapter_order'] as number) ?? 0,
          lessons: [],
        };
        chapters.set(chapterId, chapter);
      }
      const lesson: ProFormationLesson = {
        id: row['video_id'] as string,
        chapterId,
        titleFr: (row['title_fr'] as string) ?? null,
        titleAr: (row['title_ar'] as string) ?? null,
        titleEn: (row['title_en'] as string) ?? null,
        descriptionFr: (row['description_fr'] as string) ?? null,
        descriptionAr: (row['description_ar'] as string) ?? null,
        descriptionEn: (row['description_en'] as string) ?? null,
        videoUrl: (row['video_url'] as string) ?? '',
        provider: normalizeProvider(row['provider']),
        thumbnailUrl: (row['thumbnail_url'] as string) ?? null,
        durationSeconds: (row['duration_seconds'] as number) ?? null,
        displayOrder: (row['video_order'] as number) ?? 0,
        completedAt: (row['completed_at'] as string) ?? null,
      };
      chapter.lessons.push(lesson);
    }
    return Array.from(chapters.values());
  }

  async setProLessonProgress(videoId: string, completed: boolean): Promise<void> {
    const { error } = await this.db.rpc('pro_formation_set_video_progress', {
      p_video_id: videoId,
      p_completed: completed,
    });
    if (error) throw error;
  }

  async completeAllProLessons(): Promise<number> {
    const { data, error } = await this.db.rpc('pro_formation_complete_all');
    if (error) throw error;
    return (data as number) ?? 0;
  }

  async resetProProgress(): Promise<void> {
    const { error } = await this.db.rpc('pro_formation_reset');
    if (error) throw error;
  }
}

function mapFormationPagePatch(
  input: CreateFormationPageInput | UpdateFormationPageInput,
): Record<string, unknown> {
  const map: Record<string, string> = {
    slug: 'slug',
    titleFr: 'title_fr',
    titleAr: 'title_ar',
    titleEn: 'title_en',
    descriptionFr: 'description_fr',
    descriptionAr: 'description_ar',
    descriptionEn: 'description_en',
    contentFr: 'content_fr',
    contentAr: 'content_ar',
    contentEn: 'content_en',
    categoryFr: 'category_fr',
    categoryAr: 'category_ar',
    categoryEn: 'category_en',
    icon: 'icon',
    audience: 'audience',
    isPublished: 'is_published',
    displayOrder: 'display_order',
  };
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const col = map[k];
    if (col) patch[col] = v;
  }
  return patch;
}

function mapFormationPage(row: Record<string, unknown>): FormationPage {
  return {
    id: row['id'] as string,
    slug: row['slug'] as string,
    titleFr: (row['title_fr'] as string) ?? null,
    titleAr: (row['title_ar'] as string) ?? null,
    titleEn: (row['title_en'] as string) ?? null,
    descriptionFr: (row['description_fr'] as string) ?? null,
    descriptionAr: (row['description_ar'] as string) ?? null,
    descriptionEn: (row['description_en'] as string) ?? null,
    contentFr: (row['content_fr'] as string) ?? null,
    contentAr: (row['content_ar'] as string) ?? null,
    contentEn: (row['content_en'] as string) ?? null,
    categoryFr: (row['category_fr'] as string) ?? null,
    categoryAr: (row['category_ar'] as string) ?? null,
    categoryEn: (row['category_en'] as string) ?? null,
    icon: (row['icon'] as string) ?? null,
    audience: normalizeAudience(row['audience']),
    isPublished: (row['is_published'] as boolean) ?? true,
    displayOrder: (row['display_order'] as number) ?? 0,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapFormationVideoPatch(
  input: CreateFormationVideoInput | UpdateFormationVideoInput,
): Record<string, unknown> {
  const map: Record<string, string> = {
    pageId: 'page_id',
    titleFr: 'title_fr',
    titleAr: 'title_ar',
    titleEn: 'title_en',
    descriptionFr: 'description_fr',
    descriptionAr: 'description_ar',
    descriptionEn: 'description_en',
    videoUrl: 'video_url',
    provider: 'provider',
    thumbnailUrl: 'thumbnail_url',
    durationSeconds: 'duration_seconds',
    displayOrder: 'display_order',
    isPublished: 'is_published',
  };
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const col = map[k];
    if (col) patch[col] = v;
  }
  return patch;
}

function mapFormationVideo(row: Record<string, unknown>): FormationVideo {
  return {
    id: row['id'] as string,
    pageId: row['page_id'] as string,
    titleFr: (row['title_fr'] as string) ?? null,
    titleAr: (row['title_ar'] as string) ?? null,
    titleEn: (row['title_en'] as string) ?? null,
    descriptionFr: (row['description_fr'] as string) ?? null,
    descriptionAr: (row['description_ar'] as string) ?? null,
    descriptionEn: (row['description_en'] as string) ?? null,
    videoUrl: (row['video_url'] as string) ?? '',
    provider: normalizeProvider(row['provider']),
    thumbnailUrl: (row['thumbnail_url'] as string) ?? null,
    durationSeconds: (row['duration_seconds'] as number) ?? null,
    displayOrder: (row['display_order'] as number) ?? 0,
    isPublished: (row['is_published'] as boolean) ?? true,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function normalizeAudience(value: unknown): FormationAudience {
  return value === 'pro' || value === 'both' ? value : 'staff';
}

function normalizeProvider(value: unknown): FormationVideoProvider {
  return value === 'vimeo' || value === 'file' ? value : 'youtube';
}
